import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { AI_CONTROL, AI_CONTROL_QUICK_SWITCH } from "@/constants/testIds";
import { KIND_META } from "@/pages/AIConnectors";
import { toast } from "sonner";
import {
  Zap, PowerOff, Bot, ShieldAlert, AlertTriangle, Plug,
  BarChart3, MessageSquare, Sparkles, FileText, Beaker, Trash2, RefreshCw,
  UserPlus, Cog, ExternalLink, Save,
} from "lucide-react";
import { Link } from "react-router-dom";
import HintIcon from "@/components/HintIcon";
import ShadowStatusPanel from "@/components/ShadowStatusPanel";

// v28 — WhatsApp markdown preview helper (mirrors backend offer_engine._STYLES).
const STYLE_WRAP = {
  italic: "_", bold: "*", monospace: "`", strikethrough: "~", none: "",
};
function renderStyle(text, style) {
  const t = (text || "").trim();
  if (!t) return "";
  const w = STYLE_WRAP[(style || "italic").toLowerCase()] ?? "_";
  return `${w}${t}${w}`;
}

// ---- Reusable module row: toggle + per-capability provider dropdown -------
function ModuleRow({
  testId, label, sub, checked, onChange, disabled = false, danger = false,
  capability, routing, setRoutingLocal, connectors, activeConnector,
}) {
  const current = capability ? routing[capability] : null;
  return (
    <div className={`flex items-start justify-between gap-4 p-4 border rounded-md transition-colors duration-150 ${
      disabled ? "bg-slate-50 border-slate-200 opacity-60" : "bg-white border-slate-200 hover:border-slate-300"
    }`}>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-semibold ${danger ? "text-red-800" : "text-slate-900"}`}>{label}</div>
        <div className="text-xs text-slate-600 mt-0.5">{sub}</div>
        {capability && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 whitespace-nowrap">AI Provider</span>
            <select
              data-testid={`ai-module-provider-${capability}`}
              disabled={disabled}
              value={current || ""}
              onChange={(e) => setRoutingLocal(capability, e.target.value || null)}
              className="flex-1 max-w-[420px] px-2 py-1 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
            >
              <option value="">
                Use active connector{activeConnector ? ` (${activeConnector.name})` : ""}
              </option>
              {connectors.filter((r) => !r.is_active).map((r) => (
                <option key={r.id} value={r.id}>
                  {KIND_META[r.kind]?.icon || "•"} {r.name} · {r.model || KIND_META[r.kind]?.default_model}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <button
        data-testid={testId}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`shrink-0 relative h-6 w-11 rounded-full transition-colors duration-150 ${
          checked ? (danger ? "bg-red-600" : "bg-emerald-500") : "bg-slate-300"
        }`}
      >
        <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-150 ${
          checked ? "translate-x-5" : ""
        }`} />
      </button>
    </div>
  );
}

// ============================================================================
export default function AIControl() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [switching, setSwitching] = useState(false);
  const [routing, setRouting] = useState({});
  const [routingDirty, setRoutingDirty] = useState(false);
  // Dev tools state
  const [devBusy, setDevBusy] = useState("");  // "" | "reset_users" | "reset_usage" | "create" | "delete_fake"
  const [armed, setArmed] = useState("");  // "" | "reset_users" | "reset_usage" | "delete_fake" — first click arms; second fires
  const [fakeCount, setFakeCount] = useState(20);
  const [fakeExam, setFakeExam] = useState("");
  const [fakeCity, setFakeCity] = useState("");
  const [fakeState, setFakeState] = useState("");
  const [fakeJoinDate, setFakeJoinDate] = useState("");  // <input type="datetime-local"> value
  const [fakeStatus, setFakeStatus] = useState("");      // "" = auto from join_date
  const [fakePhone, setFakePhone] = useState("");
  const [fakeEmail, setFakeEmail] = useState("");

  const load = async () => {
    const [ctrl, conn, r] = await Promise.all([
      api.get("/ai-controls"),
      api.get("/ai-connectors"),
      api.get("/ai-routing"),
    ]);
    setC(ctrl.data);
    setConnectors(conn.data);
    setRouting(r.data || {});
    setRoutingDirty(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = async (patch) => {
    setSaving(true);
    setC({ ...c, ...patch });
    try {
      const { data } = await api.put("/ai-controls", patch);
      setC(data);
      toast.success("Updated");
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    if (!id) return;
    setSwitching(true);
    try {
      await api.post(`/ai-connectors/${id}/activate`);
      const row = connectors.find((x) => x.id === id);
      toast.success(`Switched active AI to "${row?.name}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally {
      setSwitching(false);
    }
  };

  const setRoutingLocal = (capability, value) => {
    setRouting((prev) => ({ ...prev, [capability]: value }));
    setRoutingDirty(true);
  };

  const saveRouting = async () => {
    try {
      const payload = {};
      for (const cap of ["draft_message", "score_user", "generate_digest", "assistant_query"]) {
        payload[cap] = routing[cap] || null;
      }
      await api.put("/ai-routing", payload);
      toast.success("Provider routing saved");
      setRoutingDirty(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const arm = (action) => {
    setArmed(action);
    toast.warning("Click again within 4s to confirm — destructive action");
    setTimeout(() => setArmed((prev) => (prev === action ? "" : prev)), 4000);
  };

  const resetUsers = async () => {
    if (armed !== "reset_users") { arm("reset_users"); return; }
    setArmed("");
    setDevBusy("reset_users");
    try {
      const { data } = await api.post("/dev/reset-users");
      toast.success(`Deleted ${data.deleted.trial_users} users + ${data.deleted.messages} messages`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setDevBusy(""); }
  };
  const resetUsage = async () => {
    if (armed !== "reset_usage") { arm("reset_usage"); return; }
    setArmed("");
    setDevBusy("reset_usage");
    try {
      const { data } = await api.post("/dev/reset-ai-usage");
      toast.success(`Deleted ${data.deleted} usage rows`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setDevBusy(""); }
  };
  const createFake = async () => {
    const n = Math.max(1, Math.min(500, parseInt(fakeCount || 1, 10)));
    setDevBusy("create");
    try {
      const payload = { count: n };
      if (fakeExam) payload.exam = fakeExam;
      if (fakeCity) payload.city = fakeCity;
      if (fakeState) payload.state = fakeState;
      if (fakeJoinDate) payload.join_date = new Date(fakeJoinDate).toISOString();
      if (fakeStatus) payload.subscription_status = fakeStatus;
      if (fakePhone) payload.phone = fakePhone;
      if (fakeEmail) payload.email = fakeEmail;
      const { data } = await api.post("/dev/create-fake-users", payload);
      toast.success(`Created ${data.inserted} fake users (batch ${data.batch_tag})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setDevBusy(""); }
  };
  const deleteFake = async () => {
    if (armed !== "delete_fake") { arm("delete_fake"); return; }
    setArmed("");
    setDevBusy("delete_fake");
    try {
      const { data } = await api.post("/dev/delete-fake-users");
      toast.success(`Deleted ${data.deleted.trial_users} fake users + ${data.deleted.behavior_events} events`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDevBusy(""); }
  };

  const active = useMemo(() => connectors.find((x) => x.is_active), [connectors]);

  if (!c) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  const disabled = c.global_kill_switch;

  return (
    <div data-testid={AI_CONTROL.root} className="p-8 lg:p-12 max-w-[1100px]">
      <div className="mb-8">
        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Configuration</div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
          <Zap className="h-7 w-7 text-orange-600" /> AI Control Panel
          <HintIcon
            testId="ai-control-page-hint"
            hint={
              <>
                One page to control every AI capability:<br /><br />
                <b>Active AI Service Provider</b> — the default connector used when a capability has no override.<br />
                <b>Global kill switch</b> — a big red button that stops ALL AI calls immediately.<br />
                <b>Per-capability toggles</b> — disable a single AI feature (drafting, scoring, digests, assistant, cadence) without affecting the others.<br />
                <b>Capability routing</b> — route each capability to a different connector (e.g., cheap Ollama for scoring, Claude for drafting).<br />
                <b>Dev tools</b> — reset AI usage, spawn fake users, clear DB (test-mode only).
              </>
            }
          />
        </h1>
      </div>

      {/* Active AI quick-switch */}
      <div className="mb-6 bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-start gap-4 flex-wrap">          <div className="h-10 w-10 rounded-md bg-orange-100 flex items-center justify-center shrink-0">
            <Plug className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900">Active AI Service Provider</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Every AI capability uses this by default. Override per-module below.
              {active
                ? <> Currently: <b>{active.name}</b> ({active.kind} · {active.model || "default"}).</>
                : <> No connector configured — using env fallback. Add one via Manage.</>}
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <select
              data-testid={AI_CONTROL_QUICK_SWITCH.select}
              disabled={switching || connectors.length === 0}
              value={active?.id || ""}
              onChange={(e) => activate(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none min-w-[220px] disabled:opacity-60"
            >
              {connectors.length === 0 && <option value="">No connectors configured</option>}
              {connectors.map((cn) => (
                <option key={cn.id} value={cn.id}>{cn.name} · {cn.kind}</option>
              ))}
            </select>
            <Link
              to="/ai-connectors"
              className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 hover:text-slate-800 whitespace-nowrap inline-flex items-center gap-1"
            >
              Manage <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* CPS V2 Shadow Monitoring (Stage 5) */}
      <div className="mb-6">
        <ShadowStatusPanel />
      </div>

      {/* Global kill switch */}
      <div className={`mb-6 rounded-lg border p-5 ${c.global_kill_switch ? "bg-red-50 border-red-300" : "bg-white border-slate-200"}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <PowerOff className={`h-5 w-5 ${c.global_kill_switch ? "text-red-600" : "text-slate-400"}`} />
              <div className="font-heading font-bold text-lg tracking-tight">Global Kill Switch</div>
            </div>
            <div className="text-sm text-slate-700">
              When ON, <b>every AI capability stops immediately</b> — no cadence, drafting, scoring, digests, or Ask Pippy.
            </div>
            {c.global_kill_switch && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-100 border border-red-200 px-2 py-1 rounded">
                <AlertTriangle className="h-3 w-3" /> ALL AI IS CURRENTLY STOPPED
              </div>
            )}
          </div>
          <button
            data-testid={AI_CONTROL.killSwitch}
            onClick={() => update({ global_kill_switch: !c.global_kill_switch })}
            disabled={saving}
            className={`shrink-0 relative h-7 w-14 rounded-full transition-colors duration-150 ${c.global_kill_switch ? "bg-red-600" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-150 ${c.global_kill_switch ? "translate-x-7" : ""}`} />
          </button>
        </div>
      </div>

      {/* Per-module toggles + provider dropdown */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Cog className="h-4 w-4 text-orange-600" />
              <h2 className="font-heading font-bold text-lg tracking-tight">AI Modules</h2>
            </div>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
              Turn any module off, and/or route it to a specific AI provider. Typical setup: cheap
              Ollama/Mock for <i>scoring</i> and <i>digests</i>, premium Claude for <i>drafting</i>.
            </p>
          </div>
          {routingDirty && (
            <button
              data-testid="ai-modules-save-routing-btn"
              onClick={saveRouting}
              className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500"
            >
              <Save className="h-3.5 w-3.5" /> Save provider routing
            </button>
          )}
        </div>

        <div className="space-y-3">
          <ModuleRow
            testId={AI_CONTROL.cadenceToggle} disabled={disabled}
            label="Cadence auto-send"
            sub="Scheduled WhatsApp/Email messages sent to trial users at H+N hours."
            checked={c.cadence_enabled} onChange={(v) => update({ cadence_enabled: v })}
            capability="draft_message"
            routing={routing} setRoutingLocal={setRoutingLocal}
            connectors={connectors} activeConnector={active}
          />
          <ModuleRow
            testId={AI_CONTROL.draftingToggle} disabled={disabled}
            label="AI Draft (in user inbox)"
            sub='The "AI Draft" button on User Detail. Manual send by human still works.'
            checked={c.drafting_enabled} onChange={(v) => update({ drafting_enabled: v })}
            capability="draft_message"
            routing={routing} setRoutingLocal={setRoutingLocal}
            connectors={connectors} activeConnector={active}
          />
          <ModuleRow
            testId={AI_CONTROL.scoringToggle} disabled={disabled}
            label="Auto-scoring on inbound replies"
            sub="When a user replies, AI recomputes conversion score and tags. Cheap models work great here."
            checked={c.scoring_enabled} onChange={(v) => update({ scoring_enabled: v })}
            capability="score_user"
            routing={routing} setRoutingLocal={setRoutingLocal}
            connectors={connectors} activeConnector={active}
          />
          <ModuleRow
            testId={AI_CONTROL.digestsToggle} disabled={disabled}
            label="Daily / Weekly digests"
            sub="AI-generated summaries pushed to admin WhatsApp on schedule."
            checked={c.digests_enabled} onChange={(v) => update({ digests_enabled: v })}
            capability="generate_digest"
            routing={routing} setRoutingLocal={setRoutingLocal}
            connectors={connectors} activeConnector={active}
          />
          <ModuleRow
            testId={AI_CONTROL.assistantToggle} disabled={disabled}
            label="Ask Pippy (AI supervisor chat)"
            sub="The floating assistant panel accessible from the top-right button."
            checked={c.assistant_enabled} onChange={(v) => update({ assistant_enabled: v })}
            capability="assistant_query"
            routing={routing} setRoutingLocal={setRoutingLocal}
            connectors={connectors} activeConnector={active}
          />
          <ModuleRow
            testId={AI_CONTROL.assistantWriteToggle} disabled={disabled || !c.assistant_enabled}
            label="Ask Pippy — write actions"
            sub="Allow the assistant to propose send/tag/pause actions (still needs your click)."
            checked={c.assistant_write_enabled} onChange={(v) => update({ assistant_write_enabled: v })}
          />
          <ModuleRow
            testId={AI_CONTROL.escalationPauseToggle} disabled={disabled}
            label="Auto-pause AI on escalation"
            sub="ON = safer mode. When any user gets escalated (refund, complaint, hard objection), AI is auto-paused for that user. OFF (default) = AI keeps drafting; each new AI message shows a red [ESCALATED] chip so agents can spot them."
            checked={c.escalation_auto_pause} onChange={(v) => update({ escalation_auto_pause: v })}
          />
        </div>
      </div>

      {/* ============ v28 — DISCLAIMER FOR OFF-KB REPLIES ============ */}
      <div className="mb-8 bg-white border border-slate-200 rounded-lg p-5" data-testid="ai-control-disclaimer-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-extrabold">Off-KB reply disclaimer</h2>
            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
              When the AI answers anything outside the PrepHacker knowledge base (medicine, drugs, legal, financial, tech, etc.),
              this disclaimer is automatically {c.disclaimer_position === "prepend" ? "prepended" : "appended"} to the reply
              before it is sent — a guarantee enforced in code, not just in the prompt.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-xs font-semibold">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!c.disclaimer_enabled}
              onChange={(e) => update({ disclaimer_enabled: e.target.checked })}
              data-testid="disclaimer-enabled-toggle"
            />
            {c.disclaimer_enabled ? "Enabled" : "Disabled"}
          </label>
        </div>
        <div className={c.disclaimer_enabled ? "" : "opacity-50 pointer-events-none"}>
          <label className="block mb-3">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Disclaimer text</span>
            <textarea
              data-testid="disclaimer-text-input"
              value={c.disclaimer_text || ""}
              onChange={(e) => update({ disclaimer_text: e.target.value })}
              rows={3}
              className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">WhatsApp style</span>
              <select
                data-testid="disclaimer-style-select"
                value={c.disclaimer_style || "italic"}
                onChange={(e) => update({ disclaimer_style: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="italic">_italic_ (recommended)</option>
                <option value="bold">*bold*</option>
                <option value="monospace">`monospace`</option>
                <option value="strikethrough">~strikethrough~</option>
                <option value="none">plain text</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">Position</span>
              <select
                data-testid="disclaimer-position-select"
                value={c.disclaimer_position || "append"}
                onChange={(e) => update({ disclaimer_position: e.target.value })}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="append">After the reply</option>
                <option value="prepend">Before the reply</option>
              </select>
            </label>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">WhatsApp preview</div>
            <div className="whitespace-pre-wrap text-slate-800">
              {c.disclaimer_position === "prepend" && renderStyle(c.disclaimer_text, c.disclaimer_style) + "\n\n"}
              [Sample AI reply body will appear here]
              {c.disclaimer_position !== "prepend" && "\n\n" + renderStyle(c.disclaimer_text, c.disclaimer_style)}
            </div>
          </div>
        </div>
      </div>

      {/* ============ AI USAGE — moved to Statistics page ============ */}
      <div className="mb-8 bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
        <BarChart3 className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-indigo-900">AI usage moved to Statistics</div>
          <div className="text-xs text-indigo-800 mt-0.5">
            Tokens, requests, latency and per-capability breakdown now live on the Statistics page
            along with signups, conversions and label spread — with Excel &amp; PDF exports.
          </div>
        </div>
        <Link
          to="/statistics"
          data-testid="ai-control-open-statistics-link"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-500"
        >
          Open Statistics <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {/* ============ DEV TOOLS ============ */}
      <div data-testid="dev-tools-root" className="rounded-lg border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Beaker className="h-4 w-4 text-amber-700" />
          <h2 className="font-heading font-bold text-lg tracking-tight text-amber-900">Test / Dev Tools</h2>
          <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border border-amber-300 text-amber-800 bg-amber-100">Test purpose only</span>
        </div>
        <p className="text-xs text-amber-800 mb-4 leading-relaxed">
          Use during testing to reset state or spin up fake users. These operations are destructive and
          logged in the audit trail — do not use in production.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3 border border-amber-200 bg-white rounded-md p-4">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="h-3.5 w-3.5 text-amber-700" />
              <div className="text-sm font-semibold text-slate-900">Create fake users</div>
              <span className="text-[10px] text-slate-500">Any field left blank uses a random realistic value.</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Count</label>
                <input
                  type="number" min="1" max="500"
                  value={fakeCount}
                  onChange={(e) => setFakeCount(parseInt(e.target.value || "1", 10))}
                  data-testid="dev-fake-count-input"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Exam</label>
                <select
                  value={fakeExam}
                  onChange={(e) => setFakeExam(e.target.value)}
                  data-testid="dev-fake-exam"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                >
                  <option value="">(random)</option>
                  {["NCLEX", "JEE Main", "JEE Advanced", "NEET", "GATE", "CAT", "UPSC", "SSC", "CLAT"].map((x) => (
                    <option key={x} value={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Join date &amp; time</label>
                <input
                  type="datetime-local"
                  value={fakeJoinDate}
                  onChange={(e) => setFakeJoinDate(e.target.value)}
                  data-testid="dev-fake-join-date"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular"
                />
                <div className="text-[10px] text-slate-400 mt-0.5">Empty = spread over last 96h</div>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">City</label>
                <input
                  type="text" placeholder="(random)"
                  value={fakeCity}
                  onChange={(e) => setFakeCity(e.target.value)}
                  data-testid="dev-fake-city"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">State</label>
                <input
                  type="text" placeholder="(random)"
                  value={fakeState}
                  onChange={(e) => setFakeState(e.target.value)}
                  data-testid="dev-fake-state"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>
            </div>
            <div className="flex items-end gap-2 mt-3 flex-wrap">
              <div className="w-40">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Subscription status</label>
                <select
                  value={fakeStatus}
                  onChange={(e) => setFakeStatus(e.target.value)}
                  data-testid="dev-fake-status"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                >
                  <option value="">(auto from join date)</option>
                  <option value="trial">trial</option>
                  <option value="expired">expired</option>
                  <option value="paid">paid</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </div>
              <div className="w-48">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Phone (E.164)</label>
                <input
                  type="tel" placeholder="(random +91…)"
                  value={fakePhone}
                  onChange={(e) => setFakePhone(e.target.value)}
                  data-testid="dev-fake-phone"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                />
              </div>
              <div className="w-56">
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Email</label>
                <input
                  type="email" placeholder="(random @example.com)"
                  value={fakeEmail}
                  onChange={(e) => setFakeEmail(e.target.value)}
                  data-testid="dev-fake-email"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                />
              </div>
              <div className="flex-1" />
              <button
                onClick={createFake}
                disabled={devBusy === "create"}
                data-testid="dev-create-fake-btn"
                className="inline-flex items-center justify-center gap-1 text-xs font-semibold px-4 py-2 rounded-md bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
              >
                {devBusy === "create" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <UserPlus className="h-3 w-3" />}
                Create {fakeCount} fake user{fakeCount === 1 ? "" : "s"}
              </button>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">
              Every fake user is tagged <code className="font-mono bg-slate-100 px-1 rounded">dev_fake</code> + <code className="font-mono bg-slate-100 px-1 rounded">dev_batch_&lt;timestamp&gt;</code> — use the &quot;Delete fake users&quot; button to purge them without touching real imported users.
            </div>
          </div>

          <div className="border border-amber-200 bg-white rounded-md p-3 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Trash2 className="h-3.5 w-3.5 text-red-600" />
              <div className="text-sm font-semibold text-slate-900">Delete fake users</div>
            </div>
            <div className="text-[11px] text-slate-600">Deletes only users tagged <code className="font-mono bg-slate-100 px-1 rounded">dev_fake</code>. Real imports (tag <code className="font-mono bg-slate-100 px-1 rounded">imported</code>) are untouched.</div>
            <button
              onClick={deleteFake}
              disabled={devBusy === "delete_fake"}
              data-testid="dev-delete-fake-btn"
              className={`mt-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white ${
                armed === "delete_fake" ? "bg-red-800 hover:bg-red-900 animate-pulse" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {devBusy === "delete_fake" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              {armed === "delete_fake" ? "Click again to confirm" : "Delete fake users"}
            </button>
          </div>

          <div className="border border-amber-200 bg-white rounded-md p-3 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Trash2 className="h-3.5 w-3.5 text-red-600" />
              <div className="text-sm font-semibold text-slate-900">Reset users &amp; chats</div>
            </div>
            <div className="text-[11px] text-slate-600">Delete ALL trial users (real + fake), their messages and chat history. Cadence resumes cleanly.</div>
            <button
              onClick={resetUsers}
              disabled={devBusy === "reset_users"}
              data-testid="dev-reset-users-btn"
              className={`mt-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white ${
                armed === "reset_users" ? "bg-red-800 hover:bg-red-900 animate-pulse" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {devBusy === "reset_users" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              {armed === "reset_users" ? "Click again to confirm" : "Reset users"}
            </button>
          </div>

          <div className="border border-amber-200 bg-white rounded-md p-3 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-3.5 w-3.5 text-red-600" />
              <div className="text-sm font-semibold text-slate-900">Reset AI usage</div>
            </div>
            <div className="text-[11px] text-slate-600">Delete every logged AI call (tokens, latency, errors). Useful before a test run.</div>
            <button
              onClick={resetUsage}
              disabled={devBusy === "reset_usage"}
              data-testid="dev-reset-usage-btn"
              className={`mt-1 inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white ${
                armed === "reset_usage" ? "bg-red-800 hover:bg-red-900 animate-pulse" : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {devBusy === "reset_usage" ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              {armed === "reset_usage" ? "Click again to confirm" : "Reset usage"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
